// MarketTabs.js
import React, { useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import "@fontsource/inter";

function MarketTabs({ isDrawerOpen, setIsDrawerOpen, isDarkMode }) {
  const pathname = window.location.pathname;

  const [activeTab, setActiveTab] = useState(() => {
    if (pathname === "/") return "Home-tab";
    if (pathname === "/about") return "About-Us-tab";
    if (pathname === "/concierge-service") return "Concierge-service-tab";
    if (pathname === "/contact") return "Contact-Us-tab";
    if (pathname === "/jackpot-vip") return "Vip-tab";
    if (pathname === "/request-quote") return "Book-tab";
    if (pathname === "/terms-of-use") return "";
    return "Home-tab";
  });

  const [goToAbout, setGoToAbout] = React.useState(false);
  const [goToHome, setGoToHome] = React.useState(false);

  if (goToAbout) return <Navigate to="/about" />;
  if (goToHome) return <Navigate to="/" />;

  const options = isDarkMode === 'dark'
    ? {
      activeClasses:
        'inline-block p-4 border-b-2 rounded-t-lg text-[#b42520] hover:text-red-600 border-[#b42520]',
      inactiveClasses:
        'inline-block p-4 border-b-2 border-transparent rounded-t-lg text-gray-500 transition-all ease-in duration-150 hover:border-gray-400 hover:text-gray-700',
    }
    : {
      activeClasses:
        'inline-block px-3 py-4 md:px-4 md:py-4 text-base md:text-xl border-b-2 rounded-t-lg text-[#b42520] hover:text-red-600 border-[#b42520]',
      inactiveClasses:
        'inline-block px-3 py-4 md:px-4 md:py-4 text-base md:text-xl border-b-2 border-transparent rounded-t-lg text-gray-500 transition-all ease-in duration-150 hover:border-gray-400 hover:text-gray-300',
    };

  return (
    <Fragment>
      {/* Drawer for mobile / smaller desktop */}
      <Transition.Root show={isDrawerOpen} as={Fragment}>
        <Dialog as="div" className="fixed inset-0 overflow-hidden z-50" onClose={() => setIsDrawerOpen(false)}>
          <div className="absolute inset-0 overflow-hidden">
            <Transition.Child
              as={Fragment}
              enter="ease-in-out duration-500"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in-out duration-500"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Transition.Child
                as={Fragment}
                enter="ease-in-out duration-500"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in-out duration-500"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity" />
              </Transition.Child>
            </Transition.Child>

            <div className="fixed inset-y-0 right-0 max-w-full flex">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <div className="relative w-screen max-w-md bg-white p-4">
                  <button onClick={() => setIsDrawerOpen(false)} className="absolute top-0 right-0 p-4">
                    <FontAwesomeIcon icon={faTimes} className="h-10 w-10" />
                  </button>

                  <div className="text-4xl font-bold pb-5">Menu</div>

                  <ul className="flex flex-col mt-6 space-y-4">
                    <li><Link to="/" className="block p-2 rounded-md hover:bg-gray-100 hover:text-blue-600 transition duration-300">Home</Link></li>
                    <li><Link to="/about" className="block p-2 rounded-md hover:bg-gray-100 hover:text-blue-600 transition duration-300">About Us</Link></li>
                    <li><Link to="/jackpot-vip" className="block p-2 rounded-md hover:bg-gray-100 hover:text-blue-600 transition duration-300">Jackpot VIP</Link></li>
                    <li><Link to="/concierge-service" className="block p-2 rounded-md hover:bg-gray-100 hover:text-blue-600 transition duration-300">Concierge Service</Link></li>
                    <li><Link to="/contact" className="block p-2 rounded-md hover:bg-gray-100 hover:text-blue-600 transition duration-300">Contact Us</Link></li>
                    <li><Link to="/faq" className="block p-2 rounded-md hover:bg-gray-100 hover:text-blue-600 transition duration-300">FAQ</Link></li>
                    <p><Link to="/request-quote" className="block p-2 rounded-md hover:bg-gray-100 hover:text-blue-600 transition duration-300 font-extrabold text-white text-center bg-red-600 hover:bg-red-700">Request a Quote</Link></p>
                  </ul>
                </div>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Desktop tabs only on xl+ (Header controls when to show tabs) */}
      <div className="hidden xl:block mt-4">
        <div className="mt-3 border-gray-200 dark:border-gray-700">
          <ul
            className="list-none flex flex-row whitespace-nowrap items-center space-x-2 lg:space-x-3 xl:space-x-4"
            style={{ fontFamily: "oswald", fontWeight: "bold" }}
            id="myTab"
            data-tabs-toggle="#myTabContent"
            role="tablist"
          >
            <li role="presentation">
              <Link to="/">
                <button
                  className={activeTab === "Home-tab" ? options.activeClasses : options.inactiveClasses}
                  id="Home-tab"
                  type="button"
                  onClick={() => setActiveTab("Home-tab")}
                >
                  Home
                </button>
              </Link>
            </li>

            <li role="presentation">
              <Link to="/about">
                <button
                  className={activeTab === "About-Us-tab" ? options.activeClasses : options.inactiveClasses}
                  id="About-Us-tab"
                  type="button"
                  onClick={() => setActiveTab("About-Us-tab")}
                >
                  About Us
                </button>
              </Link>
            </li>

            <li role="presentation">
              <Link to="/jackpot-vip">
                <button
                  className={activeTab === "Vip-tab" ? options.activeClasses : options.inactiveClasses}
                  id="Vip-tab"
                  type="button"
                  onClick={() => setActiveTab("Vip-tab")}
                >
                  Jackpot VIP
                </button>
              </Link>
            </li>

            <li role="presentation">
              <Link to="/concierge-service">
                <button
                  className={activeTab === "Concierge-service-tab" ? options.activeClasses : options.inactiveClasses}
                  id="Concierge-service-tab"
                  type="button"
                  onClick={() => setActiveTab("Concierge-service-tab")}
                >
                  Concierge Service
                </button>
              </Link>
            </li>

            <li role="presentation">
              <Link to="/contact">
                <button
                  className={activeTab === "Contact-Us-tab" ? options.activeClasses : options.inactiveClasses}
                  id="Contact-Us-tab"
                  type="button"
                  onClick={() => setActiveTab("Contact-Us-tab")}
                >
                  Contact Us
                </button>
              </Link>
            </li>

            <li role="presentation">
              <Link to="/faq">
                <button
                  className={activeTab === "Faq-tab" ? options.activeClasses : options.inactiveClasses}
                  id="Faq-tab"
                  type="button"
                  onClick={() => setActiveTab("Faq-tab")}
                >
                  FAQ
                </button>
              </Link>
            </li>

            <li role="presentation" className="flex items-center">
              <Link to="/request-quote" className="flex items-center">
                <button
                  id="Book-now-btn"
                  type="button"
                  onClick={() => setActiveTab("Book-tab")}
                  className="
                    px-4 py-3 xl:px-6 xl:py-4
                    rounded-md
                    bg-red-600 text-white
                    hover:bg-red-700
                    font-extrabold
                    text-base xl:text-xl
                    leading-none
                    border-0
                    focus:outline-none
                    focus:ring-0
                    active:border-0
                  "
                >
                  Request a Quote
                </button>
              </Link>
            </li>
          </ul>
        </div>
      </div>
    </Fragment>
  );
}

export default MarketTabs;
